import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

interface Submission {
  id: string;
  title: string;
  description: string;
  status: string;
  submitted_by_user_name: string;
  created_at: string;
  updated_at: string;
  posting?: {
    instagram_url?: string;
    posted_at?: string;
  };
}

const PostsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  useEffect(() => {
    fetchPosts();
    fetchCount();
  }, [page]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params: any = { 
        page, 
        page_size: pageSize,
        status: 'POSTED' // Only fetch POSTED submissions
      };
      
      const response = await axios.get(`${API}/submissions`, { params });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const response = await axios.get(`${API}/submissions/count`, { 
        params: { status: 'POSTED' }
      });
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch count:', error);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div>
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 mb-4 lg:mb-6 border border-gray-200">
        <div className="mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Inter, sans-serif' }}>
            Posts
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">View all published posts</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No posts found</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Title</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Submitted By</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Posted At</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Instagram</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <tr key={submission.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-900">{submission.title}</p>
                        <p className="text-sm text-gray-600 truncate max-w-xs">{submission.description}</p>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{submission.submitted_by_user_name}</td>
                      <td className="py-3 px-4 text-gray-700 text-sm">
                        {submission.posting?.posted_at 
                          ? new Date(submission.posting.posted_at).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        {submission.posting?.instagram_url ? (
                          <a 
                            href={submission.posting.instagram_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm underline"
                          >
                            View Post
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => navigate(`/submissions/${submission.id}`)}
                          className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
                        >
                          <Eye size={16} className="mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {submissions.map((submission) => (
                <div key={submission.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <h3 className="font-medium text-gray-900 mb-2">{submission.title}</h3>
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">{submission.description}</p>
                  <div className="space-y-2 text-sm mb-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">By:</span>
                      <span className="font-medium">{submission.submitted_by_user_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Posted:</span>
                      <span className="font-medium">
                        {submission.posting?.posted_at 
                          ? new Date(submission.posting.posted_at).toLocaleDateString()
                          : 'N/A'}
                      </span>
                    </div>
                    {submission.posting?.instagram_url && (
                      <div>
                        <a 
                          href={submission.posting.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm underline"
                        >
                          View Instagram Post →
                        </a>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/submissions/${submission.id}`)}
                    className="w-full min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
                <p className="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
                  Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} posts
                </p>
                <div className="flex gap-2 order-1 sm:order-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PostsPage;
